// Menu UI
export const MENU = {
  // Title screen
  TITLE_FONT_SIZE: 72,
  TAGLINE_FONT_SIZE: 16,
  PROMPT_FONT_SIZE: 20,
  HINT_FONT_SIZE: 14,
  CREDIT_FONT_SIZE: 12,
  PROMPT_PULSE_SPEED: 4,
  PROMPT_PULSE_MIN: 0.3,
  ORBIT_COUNT: 4,
  ORBIT_RADIUS_X: 200,
  ORBIT_RADIUS_Y: 80,
  ORBIT_SPEED: 0.5,
  ORBIT_SHAPE_SIZE: 18,
  ORBIT_PULSE_AMP: 3,
  VOID_BASE_Y: 1340,
  VOID_OSCILLATION_AMP: 20,
  VOID_OSCILLATION_SPEED: 2,
  // Mode select
  MODE_SELECT_TITLE_SIZE: 40,
  MODE_LABEL_SIZE: 28,
  MODE_DESC_SIZE: 14,
  MODE_BOX_WIDTH: 400,
  MODE_BOX_HEIGHT: 80,
  MODE_BOX_GLOW_DIST: 20,
  // Pause
  PAUSE_OVERLAY_ALPHA: 0.7,
  PAUSE_TITLE_SIZE: 48,
  PAUSE_OPTION_SIZE: 24,
  // Transitions
  TRANSITION_DURATION: 0.4,
  // Ambient particles (Sakurai-audited)
  AMBIENT_SPAWN_RATE: 0.06,
  AMBIENT_MAX: 15,
  AMBIENT_LIFE_MIN: 0.4,
  AMBIENT_LIFE_MAX: 0.8,
  AMBIENT_RADIUS_MIN: 2,
  AMBIENT_RADIUS_MAX: 4,
  AMBIENT_SPEED_MIN: 30,
  AMBIENT_SPEED_MAX: 80,
  AMBIENT_DARK_CHANCE: 0.3,
  AMBIENT_DARK_COLOR: 0x110022,
  // Colors
  TITLE_LINE1_COLOR: 0xff00ff,   // magenta — GEOMETRY
  TITLE_LINE2_COLOR: 0x00ffff,   // cyan — BLAST
  TAGLINE_COLOR: 0x888888,
  HINT_COLOR: 0x666666,
  CREDIT_COLOR: 0x444444,
  SELECTED_COLOR: 0xffffff,
  UNSELECTED_COLOR: 0x666666,
  // Selection fill behind selected option (mode color at low alpha)
  SELECTION_FILL_ALPHA: 0.06,
  SELECTION_CORNER_RADIUS: 8,
} as const;

// Tank select — SF2/Melee character select grid
export const TANK_SELECT = {
  // Grid layout (max 6 columns × 2 rows = 12 cells)
  COLS: 6,
  ROWS: 2,
  CELL_SIZE: 140,
  CELL_GAP: 10,
  GRID_TOP_Y: 280,
  // Cell visuals
  CELL_BG_COLOR: 0x0a0a19,
  CELL_BG_ALPHA: 0.8,
  CELL_CORNER_RADIUS: 8,
  CELL_HEX_RADIUS: 22,
  CELL_NAME_SIZE: 12,
  CELL_NAME_COLOR: 0x888888,
  CELL_LOCKED_ALPHA: 0.25,
  // Info panels (left P1, right P2)
  INFO_PANEL_WIDTH: 280,
  INFO_PANEL_MARGIN: 40,
  INFO_PANEL_TOP_Y: 260,
  INFO_NAME_SIZE: 32,
  INFO_DESC_SIZE: 13,
  INFO_DESC_COLOR: 0x888888,
  INFO_HEX_RADIUS: 35,
  INFO_TIER_SIZE: 14,
  INFO_READY_SIZE: 24,
  INFO_READY_COLOR: 0x44ff88,
  INFO_LABEL_SIZE: 20,
  // Cursor
  CURSOR_WIDTH: 3,
  CURSOR_BRACKET_SIZE: 16,
  CURSOR_PULSE_SPEED: 4,
  CURSOR_PULSE_MIN: 0.5,
  CURSOR_LOCKED_ALPHA: 1.0,
} as const;

// Splash screen — BonkJS crash-in
export const SPLASH = {
  // ─── BonkJS brand colors ───
  ACCENT: 0xf59e0b,              // bonkjs amber
  ACCENT_GLOW: 0xfbbf24,         // bonkjs amber glow
  ACCENT_DARK: 0x7a4f06,         // darker amber for contrast
  JS_COLOR: 0x71717a,            // zinc-500 (muted "js" text)
  FLASH_DARK: 0x1a0f00,          // dark amber shadow (Sakurai: mix dark into flash)

  // ─── Letter styling ───
  LETTER_FONT_SIZE: 140,         // BIG and heavy
  JS_FONT_SIZE: 52,
  LETTER_SPACING: 8,             // tight tracking for impact
  LOGO_Y: 0.45,                  // vertical position as fraction of canvas height

  // ─── Phase timing (seconds) ───
  PHASE0_DURATION: 0.12,         // anticipation speed lines (snappy)
  LETTER_STAGGER: 0.065,         // delay between each letter slam (rapid-fire)
  LETTER_ANIM_DURATION: 0.35,    // per-letter squash-stretch settle time
  JS_DELAY: 0.08,                // delay after last letter before "js" slides in
  JS_SLIDE_DURATION: 0.2,        // "js" slide-in duration (snappy)
  BURST_DELAY: 0.08,             // delay after "js" before big burst (immediate!)
  BURST_DURATION: 0.4,           // impact burst phase
  SETTLE_DURATION: 0.35,         // settle + end flash
  HOLD_DURATION: 0.4,            // hold the final logo before fade
  FADE_DURATION: 0.45,           // fade out to title screen

  // ─── Letter crash-in ───
  LETTER_DROP_SPEED: 4500,       // px/s — FAST crash from above
  LETTER_OVERSHOOT: 35,          // px below target on first impact
  LETTER_BOUNCE_HEIGHT: 20,      // px upward on first bounce

  // ─── Impact particles (per letter) ───
  IMPACT_DEBRIS_COUNT: 16,       // MORE debris particles per letter impact
  IMPACT_DEBRIS_SPEED_MIN: 4,
  IMPACT_DEBRIS_SPEED_MAX: 14,
  IMPACT_DEBRIS_LIFE_MIN: 0.15,
  IMPACT_DEBRIS_LIFE_MAX: 0.45,
  IMPACT_DEBRIS_SCALE_MIN: 0.2,
  IMPACT_DEBRIS_SCALE_MAX: 0.7,
  IMPACT_DEBRIS_GRAVITY: 0.2,
  IMPACT_FLASH_COUNT: 5,         // more bright flash particles per letter
  IMPACT_DARK_COUNT: 4,          // more dark contrast particles per letter
  IMPACT_SHOCKWAVE_RADIUS: 80,   // bigger per-letter shockwave
  IMPACT_SHOCKWAVE_SPEED: 350,
  IMPACT_SHOCKWAVE_LIFE: 0.22,

  // ─── Camera shake (per letter) ───
  LETTER_SHAKE: 10,              // stronger shake per letter
  LETTER_SHAKE_DECAY: 0.72,
  LETTER_FLASH_ALPHA: 0.12,      // brighter flash per letter

  // ─── Speed lines (anticipation) ───
  SPEED_LINE_COUNT: 14,          // more speed lines
  SPEED_LINE_LENGTH_MIN: 100,
  SPEED_LINE_LENGTH_MAX: 250,
  SPEED_LINE_WIDTH: 3,
  SPEED_LINE_SPEED: 1500,        // px/s — faster converge toward center
  SPEED_LINE_LIFE: 0.15,

  // ─── Big burst (Phase 3) ───
  BURST_PARTICLE_COUNT: 120,     // MORE particles in burst
  BURST_SPEED_MIN: 5,
  BURST_SPEED_MAX: 24,
  BURST_LIFE_MIN: 0.18,
  BURST_LIFE_MAX: 0.6,
  BURST_SCALE_MIN: 0.25,
  BURST_SCALE_MAX: 1.5,
  BURST_GEM_COUNT: 60,           // more chiclet gems
  BURST_STREAK_COUNT: 20,        // more radial speed lines
  BURST_STREAK_SPEED_MIN: 25,
  BURST_STREAK_SPEED_MAX: 45,
  BURST_STREAK_LIFE: 0.2,
  BURST_CROWN_COUNT: 32,         // bigger outer crown ring
  BURST_SHOCKWAVE_RADIUS: 200,   // bigger shockwave
  BURST_SHOCKWAVE_SPEED: 500,
  BURST_SHOCKWAVE_LIFE: 0.35,
  BURST_SHAKE: 18,               // HEAVY shake on burst
  BURST_SHAKE_DECAY: 0.82,
  BURST_FLASH_ALPHA: 0.22,       // brighter burst flash

  // ─── End flash (Phase 4) ───
  END_FLASH_COUNT: 10,
  END_FLASH_SCALE: 3.5,
  END_FLASH_LIFE: 0.1,
  END_SHOCKWAVE_RADIUS: 120,
  END_SHOCKWAVE_SPEED: 600,
  END_SHOCKWAVE_LIFE: 0.12,
} as const;

// Cosmic Lottery
export const LOTTERY = {
  // Slot timing
  LOCK_TIMES: [0.4, 0.6, 1.0, 1.0] as readonly number[],  // seconds when each slot locks (4th = mortar, instant with 3rd)
  PICK_TIMEOUT: 5.0,                                   // seconds before auto-pick center
  // Slot positioning (world-space offsets from tank)
  SLOT_CENTER_Y: -100,
  SLOT_SIDE_X: 50,
  SLOT_SIDE_Y: -90,
  SLOT_WIDTH: 60,
  SLOT_HEIGHT: 40,
  // Rarity weights
  WEIGHT_COMMON: 50,
  WEIGHT_UNCOMMON: 30,
  WEIGHT_RARE: 15,
  WEIGHT_EPIC: 4,
  WEIGHT_LEGENDARY: 1,
  // Pity
  PITY_THRESHOLD: 5,
  // Rarity colors
  COLOR_COMMON: 0xffffff,
  COLOR_UNCOMMON: 0x33ff99,
  COLOR_RARE: 0x00ffff,
  COLOR_EPIC: 0xff00ff,
  COLOR_LEGENDARY: 0xffd700,
} as const;

// Training mode
export const TRAINING = {
  DUMMY_COLOR: 0x888888,         // neutral gray
} as const;

// Target Blast mode
export const TARGET_BLAST = {
  TARGET_RADIUS: 20,             // visual/collision radius
  TARGET_COLOR: 0xffaa00,        // amber glow
  TARGET_HIT_COLOR: 0x44ff44,    // green flash on hit
  TARGET_GLOW_ALPHA: 0.4,        // outer glow intensity
  TARGET_PULSE_SPEED: 3.0,       // pulse animation speed
  TARGET_HIT_FLASH_DURATION: 0.3, // seconds
  // Exit gate
  GATE_RADIUS: 40,               // collision radius for gate entry
  GATE_INACTIVE_COLOR: 0xcc8800, // dim amber ring
  GATE_ACTIVE_COLOR: 0x44ff88,   // bright green portal
  GATE_GLOW_COLOR: 0x00ff66,     // green glow ring
  GATE_PULSE_SPEED: 4.0,         // pulse speed when active
  GATE_RING_WIDTH: 3,            // ring stroke width
} as const;

// Target Blast intro sequence
export const TARGET_BLAST_INTRO = {
  STEPS: [
    { text: 'EXECUTING TARGET BLASTER', color: 0xffaa00, duration: 0.8, fontSize: 36 },
    { text: 'TARGET BLASTER ENGAGED', color: 0x44ff88, duration: 0.8, fontSize: 36 },
    { text: '3', color: 0xffffff, duration: 1.0, fontSize: 72 },
    { text: '2', color: 0xffffff, duration: 1.0, fontSize: 72 },
    { text: '1', color: 0xffffff, duration: 1.0, fontSize: 72 },
    { text: 'GO!', color: 0x44ff88, duration: 0.5, fontSize: 72 },
  ],
} as const;

// Ascent mode — vertical survival
export const ASCENT = {
  WORLD_HEIGHT: 8000,
  CEILING_BLAST_ZONE: 200,       // absolute minimum ceiling (never above Y=200)
  BAND_HEIGHT: 2160,             // 1.5× viewport height (1440 × 1.5) — playable band above void
  VOID_INITIAL_Y: 7800,         // 200px below bottom platform (~7600) — visible, not threatening
  VOID_BASE_RISE: 150,           // moderate first rise
  VOID_ACCELERATION: 40,        // additional px per turn cycle — accelerating squeeze
  STAGE_WIDTH_MIN: 1600,
  STAGE_WIDTH_MAX: 2000,
  TIER_COUNT: [15, 20] as readonly [number, number],
  TIER_SPACING: [300, 500] as readonly [number, number],
  PLATFORM_WIDTH: [200, 800] as readonly [number, number],
  PLATFORMS_PER_TIER: [1, 3] as readonly [number, number],
  BOTTOM_TIER_WIDTH_FRAC: 0.7,  // wide bottom for spawning
  TOP_TIER_WIDTH_FRAC: 0.2,     // narrow top for pressure
  BRIDGE_TIER_CHANCE: 0.15,
  GAP_TIER_CHANCE: 0.15,
} as const;

// Classic Artillery mode — HP damage, fixed knockback, simplified turn flow
export const CLASSIC = {
  MAX_HP: 100,
  KNOCKBACK_SCALE: 0.4,        // Fixed multiplier (replaces percentMult)
  KNOCKBACK_CAP: 8.0,          // Max knockback velocity (prevents launches)
} as const;
